import { Inject, Injectable, OnDestroy } from '@angular/core';
import { MSAL_GUARD_CONFIG, MsalBroadcastService, MsalGuardConfiguration, MsalService } from '@azure/msal-angular';
import { EventType, InteractionType } from '@azure/msal-browser';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { resolvePostLogoutRedirectUri } from 'src/app/utils/msal-auth.util';
import { SignalRService } from 'src/app/utils/SignalR.service';
import { SgsstAuthUserService } from './auth.service';

export { resolvePostLogoutRedirectUri } from 'src/app/utils/msal-auth.util';

/**
 * Cierre de sesión unificado: limpia estado de app sin destruir el cache MSAL
 * antes del end-session de B2C/CIAM (necesario para id_token_hint / cuenta).
 */
@Injectable({
  providedIn: 'root'
})
export class SessionLogoutService implements OnDestroy {
  private logoutInProgress = false;
  private readonly destroy$ = new Subject<void>();

  constructor(
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
    private msal: MsalService,
    private msalBroadcast: MsalBroadcastService,
    private authUser: SgsstAuthUserService,
    private signalR: SignalRService
  ) {
    this.msalBroadcast.msalSubject$
      .pipe(
        filter((msg) =>
          msg.eventType === EventType.LOGOUT_FAILURE ||
          msg.eventType === EventType.LOGOUT_SUCCESS
        ),
        takeUntil(this.destroy$)
      )
      .subscribe((msg) => {
        if (msg.eventType === EventType.LOGOUT_FAILURE) {
          this.logoutInProgress = false;
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  logout(): void {
    if (this.logoutInProgress) {
      return;
    }
    this.logoutInProgress = true;

    this.signalR.stopConnection();

    const account =
      this.msal.instance.getActiveAccount() ??
      this.msal.instance.getAllAccounts()[0] ??
      undefined;

    this.authUser.clearAppSession();

    const postLogoutRedirectUri = resolvePostLogoutRedirectUri();

    if (this.msalGuardConfig.interactionType === InteractionType.Popup) {
      this.msal.logoutPopup({
        account,
        mainWindowRedirectUri: postLogoutRedirectUri
      }).subscribe({
        error: () => {
          this.logoutInProgress = false;
        },
        complete: () => {
          this.logoutInProgress = false;
        }
      });
      return;
    }

    try {
      this.msal.logoutRedirect({
        account,
        postLogoutRedirectUri
      });
    } catch {
      this.logoutInProgress = false;
    }
  }

  /** Tras ACCOUNT_REMOVED (otra pestaña / fin de logout): solo UI/app, sin otro end-session. */
  onAccountsCleared(): void {
    this.signalR.stopConnection();
    this.authUser.clearAppSession();
  }
}
